import React, { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import HTMLEditor from "./HTMLEditor";
import { useDispatch } from "react-redux";
import {
  createDocument,
  deleteDocumentById,
  updateDocumentById,
} from "../../store/Documentation/actions";
import { useNavigate } from "react-router-dom";
import { formatTime } from "../../utils/timeUtils";

const leaveWarningMessage = "Are you sure? Changes are not saved!";

const BlockContent = ({
  blockData,
  isEditable = false,
  isDeletable = false,
  errors,
  setErrors,
  resetErrorData,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [activeBlockData, setActiveBlockData] = useState({ ...blockData });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  function handleSubmit() {
    if (blockData?._id) {
      dispatch(updateDocumentById(blockData._id, activeBlockData));
    } else {
      dispatch(createDocument(activeBlockData));
    }
  }

  function handleDelete() {
    dispatch(
      deleteDocumentById(blockData._id, () => {
        navigate("/notes");
      })
    );
    setDeleteOpen(false);
  }

  useEffect(() => {
    setActiveBlockData(blockData || {});
  }, [blockData]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!hasUnsavedChanges) return;
      event.preventDefault();
      event.returnValue = leaveWarningMessage;
      return leaveWarningMessage;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  useEffect(() => {
    const isSameContent = activeBlockData?.content === blockData?.content;
    const isSameHeader = activeBlockData?.header === blockData?.header;
    setHasUnsavedChanges(
      Boolean(isEditable && blockData?._id && !(isSameContent && isSameHeader))
    );
  }, [activeBlockData, blockData, isEditable]);

  if (!Boolean(blockData?.id)) {
    return (
      <div className="block-content-wrapper empty">
        <span className="empty-message">No block selected</span>
      </div>
    );
  }

  if (!isEditable) {
    return (
      <div className="block-content-wrapper">
        <h2 className="block-view-title">{blockData.header}</h2>
        <div
          className="block-view-content"
          dangerouslySetInnerHTML={{ __html: blockData?.content || "" }}
        />
        {blockData?._id && (
          <div className="block-meta-footer">
            {blockData?.updatedAt && (
              <span>Updated At: {formatTime(blockData.updatedAt)}</span>
            )}
            {blockData?.uploaded_by && (
              <span>Updated By: {blockData.uploaded_by}</span>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="block-content-wrapper">
      {/* Header row: title input + actions */}
      <div className="block-content-header">
        <div className="block-title-input-wrapper">
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            id="inputName"
            placeholder="Enter Tab Heading"
            value={activeBlockData?.header || ""}
            error={Boolean(errors?.[blockData?.id]?.name)}
            onChange={({ target }) => {
              setActiveBlockData({
                ...activeBlockData,
                header: target.value,
              });
              setErrors(resetErrorData(errors, "header"));
            }}
            required
          />
        </div>
        <div className="block-actions">
          <Button
            variant="contained"
            disableElevation
            onClick={handleSubmit}
            sx={{
              backgroundColor: "#2364AA",
              "&:hover": { backgroundColor: "#1a4f8a" },
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {blockData?._id ? "Update" : "Create"}
          </Button>
          {blockData?._id && isDeletable && (
            <>
              <Button
                variant="contained"
                disableElevation
                color="error"
                onClick={() => setDeleteOpen(true)}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Delete
              </Button>
              {/* Delete Confirmation Dialog */}
              <Dialog
                open={deleteOpen}
                onClose={() => setDeleteOpen(false)}
                aria-labelledby="delete-dialog-title"
              >
                <DialogTitle id="delete-dialog-title">
                  Delete Note
                </DialogTitle>
                <DialogContent>
                  <DialogContentText>
                    Are you sure you want to delete{" "}
                    <strong>{blockData.header}</strong>? This action cannot be
                    undone.
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={() => setDeleteOpen(false)}
                    sx={{ textTransform: "none" }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleDelete}
                    color="error"
                    variant="contained"
                    disableElevation
                    sx={{ textTransform: "none" }}
                  >
                    Delete
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          )}
        </div>
      </div>

      {/* Rich text editor */}
      <div className="block-editor-wrapper">
        <HTMLEditor
          height="calc(100vh - 380px)"
          defaultValue={blockData?.content}
          onChange={(content) => {
            setActiveBlockData((prev) => ({
              ...prev,
              content,
            }));
            setErrors(resetErrorData(errors, "name"));
          }}
        />
      </div>

      {/* Meta footer */}
      {blockData?._id && (
        <div className="block-meta-footer">
          {blockData?.updatedAt && (
            <span>Updated At: {formatTime(blockData.updatedAt)}</span>
          )}
          {blockData?.uploaded_by && (
            <span>Updated By: {blockData.uploaded_by}</span>
          )}
        </div>
      )}
    </div>
  );
};

export default BlockContent;
